import { InternalRefetchQueriesInclude, useMutation } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

import createOrUpdateSkillMutation from "../../graphql/create-or-update-skill-mutation";
import mySkillsQuery from "../../graphql/my-skills-query";

export const NAME_MAX_LENGTH = 255;
export const DESCRIPTION_MAX_LENGTH = 255;
export const BODY_MAX_LENGTH = 50_000;

// TS port of the backend slugify_skill_name rules (chatbot/models.py). The
// backend derives the slug from the name on save; this preview mirrors that.
const slugifySkillName = (value: string): string => {
  let slug = (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  slug = slug.replace(/^-+|-+$/g, "");
  slug = slug.slice(0, 64).replace(/-+$/g, "");
  return slug || "skill";
};

type SkillEditModalInitial = {
  id?: string;
  name?: string;
  description?: string;
  body?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initial?: SkillEditModalInitial;
  organizationId?: number;
  /**
   * Queries to refetch after save. Defaults to the personal mySkills query.
   * Workspace callers pass workspaceSkills query.
   */
  refetchQueries?: InternalRefetchQueriesInclude;
  onSaved?: () => void;
};

const SkillEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  initial,
  organizationId,
  refetchQueries,
  onSaved,
}) => {
  const focusRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [body, setBody] = useState(initial?.body ?? "");

  useEffect(() => {
    if (isOpen) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setBody(initial?.body ?? "");
    }
  }, [isOpen, initial?.id, initial?.name, initial?.description, initial?.body]);

  const [createOrUpdate, { loading: saving }] = useMutation<any>(
    createOrUpdateSkillMutation,
    {
      refetchQueries: refetchQueries ?? [mySkillsQuery],
    },
  );

  const trimmedName = name.trim();
  const isOverBody = body.length > BODY_MAX_LENGTH;
  const disabled = saving || trimmedName.length === 0 || isOverBody;

  const handleSave = useCallback(async () => {
    try {
      await createOrUpdate({
        variables: {
          id: initial?.id ?? null,
          name: trimmedName,
          description,
          body,
          organizationId: organizationId ?? null,
        },
      });
      successNotificationVar({
        title: initial?.id ? "Skill updated" : "Skill created",
      });
      onSaved?.();
      onClose();
    } catch (e) {
      errorNotificationVar({
        title: "Could not save skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [
    createOrUpdate,
    initial?.id,
    trimmedName,
    description,
    body,
    organizationId,
    onSaved,
    onClose,
  ]);

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={onClose}
      initialFocus={focusRef}
      aria-label="Skill edit dialog"
      dialogClassName="md:max-w-3xl overflow-visible"
    >
      <div className="p-6 bg-white rounded-md">
        <div className="mb-4">
          <ModalTitle onClose={onClose}>
            {initial?.id ? `Edit Skill` : `New Skill`}
          </ModalTitle>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <Input
              ref={focusRef}
              label="Name"
              aria-label="Skill name"
              value={name}
              maxLength={NAME_MAX_LENGTH}
              placeholder="e.g. Daily plan"
              onChange={(e) => setName(e.target.value)}
            />
            {trimmedName.length > 0 && (
              <div
                className="mt-1 text-xs text-gray-500"
                aria-label="Skill command preview"
              >
                Command:{" "}
                <code className="bg-gray-100 px-1 rounded">
                  /{slugifySkillName(trimmedName)}
                </code>
              </div>
            )}
          </div>
          <div>
            <Input
              label="Description"
              aria-label="Skill description"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder="Short description of what this skill does"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="skill-body"
              className="block text-sm font-medium text-gray-700"
            >
              Body
            </label>
            <div className="mt-1">
              <textarea
                id="skill-body"
                aria-label="Skill body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={16}
                maxLength={BODY_MAX_LENGTH}
                placeholder="Write the instructions / system prompt for this skill"
                className="resize-y w-full p-3 sm:text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono"
              />
            </div>
            <div
              className={`mt-1 text-xs ${
                isOverBody ? "text-red-600" : "text-gray-500"
              }`}
              aria-label="Skill body character counter"
            >
              {body.length.toLocaleString()} /{" "}
              {BODY_MAX_LENGTH.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-6">
          <Button
            type="submit"
            theme={buttonTheme.primary}
            onClick={handleSave}
            disabled={disabled}
            text="Save"
          />
          <Button onClick={onClose} disabled={saving} text="Cancel" />
          {saving && <Loading mini size={5} />}
        </div>
      </div>
    </Modal>
  );
};

export default SkillEditModal;
